const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  verbose: 3,
  debug: 4
};

type LogLevel = keyof typeof LOG_LEVELS;

interface EdgeLogger {
  error: (message: string, meta?: unknown) => void;
  warn: (message: string, meta?: unknown) => void;
  info: (message: string, meta?: unknown) => void;
  verbose: (message: string, meta?: unknown) => void;
  debug: (message: string, meta?: unknown) => void;
  setLevel: (level: LogLevel) => void;
  silent: () => void;
}

let isSilent = false;
let currentLevel: LogLevel = 'info';

const getInitialLogLevel = (): LogLevel => {
  if (typeof process !== 'undefined' && process.env && process.env.LOG_LEVEL) {
    const envLevel = process.env.LOG_LEVEL.toLowerCase();
    if (envLevel in LOG_LEVELS) {
      return envLevel as LogLevel;
    }
  }

  return 'info';
};

currentLevel = getInitialLogLevel();

const shouldLog = (messageLevel: LogLevel): boolean => {
  if (isSilent) {
    return false;
  }
  const messagePriority = LOG_LEVELS[messageLevel];
  const currentPriority = LOG_LEVELS[currentLevel];
  return messagePriority <= currentPriority;
};

const serializeMeta = (meta: unknown): string => {
  if (meta instanceof Error) {
    return JSON.stringify({ message: meta.message, name: meta.name, stack: meta.stack });
  }
  return JSON.stringify(meta);
};

const formatMessage = (level: LogLevel, message: string, meta?: unknown): string => {
  const timestamp = new Date().toISOString();
  return meta
    ? `[${timestamp}] [${level}]: ${message} ${serializeMeta(meta)}`
    : `[${timestamp}] [${level}]: ${message}`;
};

const createLogMethod = (level: LogLevel, consoleMethod: (...data: any[]) => void) => {
  return (message: string, meta?: unknown) => {
    if (shouldLog(level)) {
      consoleMethod(formatMessage(level, message, meta));
    }
  };
};

export const logger: EdgeLogger = {
  error: createLogMethod('error', console.error),
  warn: createLogMethod('warn', console.warn),
  info: createLogMethod('info', console.log),
  verbose: createLogMethod('verbose', console.log),
  debug: createLogMethod('debug', console.debug),
  setLevel: (level: LogLevel) => {
    if (level in LOG_LEVELS) {
      currentLevel = level;
    } else {
      console.warn(`Invalid log level: ${level}. Keeping current level: ${currentLevel}`);
    }
  },
  silent: () => {
    isSilent = true;
  }
};

export default logger;
